import React, { useContext, useState } from "react";
import { RadialChart } from "react-vis";
import "./isp-data-card.css";
import Input from "../../../components/input/input";
import BatchActionModal, { BatchAction } from "./batch-action-modal";
import { BatchActionsApi } from "./api/batch-actions-api";
import { BackendContext } from "../../../backend/backend";
import { useToast } from "../../inmaster/hooks/useToast";
import okIcon from "../../../images/tick.svg";
import warnIcon from "../../../images/error.svg";

export default function IspDataCard({
  isp = {
    ispid: "",
    cnpj: "",
    name: "",
    phone: "",
    subscribers: "",
    devices: 0,
  },
  profile = {
    name: "",
    id: "",
  },
  batchAction = BatchAction.ASSOCIATE,
  versionModels = {
    total: 0,
    models: [],
  },
  updates = {},
  firmwares = [],
}) {
  const backend = useContext(BackendContext);
  const [selecting, setSelecting] = useState(false);
  const [loading, setLoading] = useState(false);
  const { addToast } = useToast();

  const normalizeData = (model) => {
    let data = [];

    for (let version of model.versions) {
      data.push({
        angle: version.count,
        label: `${version.version} (${version.count})`,
        color: version.color,
      });
    }

    return data;
  };

  const getActiveVersion = (model) => {
    if (updates?.versions !== undefined) {
      for (let product of updates?.versions) {
        if (model === product.model) return product.version;
        return "0.0.0";
      }
    }

    return "0.0.0";
  };

  const getLatestVersion = (model) => {
    for (let firmware of firmwares)
      if (firmware.model === model) return firmware.version;
  };

  const getVersionIcon = (model) => {
    let active = getActiveVersion(model);
    let latest = getLatestVersion(model);

    if (active === latest)
      return <img alt="versão ok" src={okIcon} width="17px"></img>;

    return (
      <img
        style={{ marginLeft: "5px" }}
        alt="Nova versão disponível"
        title="Nova versão disponível"
        src={warnIcon}
        width="17px"
      ></img>
    );
  };

  const normalizeDataLegend = (model) => {
    let data = [];

    for (let version of model.versions) {
      data.push({
        title: `${version.version} (${version.count})`,
        color: version.color,
      });
    }

    return data;
  };

  const composeModelCard = (model) => {
    return (
      <div className="model-stats-container" key={model.model}>
        <div className="model-stats" key={model.model}>
          <div className="subtitle">{model.model}</div>
          <div className="chart-subtitle">
            <span>
              <b>Versão ativa: </b>
              {getActiveVersion(model.model)} {getVersionIcon(model.model)}
            </span>
            <span>
              <b>Dispositivos: </b>
              {model.total}
            </span>
          </div>

          <div className="model-stats-chart-container">
            <RadialChart
              className="donut-chart"
              data={normalizeData(model)}
              height={160}
              width={150}
              innerRadius={70}
              radius={35}
              padAngle={0.04}
              colorType={"literal"}
            />
            <div className="radial-legends-wrapper">
              {normalizeDataLegend(model).map((item, index) => (
                <span
                  key={index}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    marginBottom: "8px",
                  }}
                >
                  <span className="radial-legend-title">
                    <span
                      className="radial-legend"
                      style={{
                        backgroundColor: item.color,
                      }}
                    ></span>
                    {item.title}
                  </span>
                </span>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  };

  async function onSubmit(data) {
    setLoading(true);
    const body = {
      isp: isp.ispid,
      ...data,
    };
    let response;
    switch (batchAction) {
      case BatchAction.ASSOCIATE:
        response = await BatchActionsApi.associate(backend, body);
        break;
      case BatchAction.DISASSOCIATE:
        response = await BatchActionsApi.disassociate(backend, body);
        break;
      case BatchAction.UPDATE:
        response = await BatchActionsApi.update(backend, body);
        break;
      case BatchAction.PROFILE_UPDATE:
        response = await BatchActionsApi.updateProfiles(backend, { ...body, profile });
        break;
      default:
        setLoading(false);
        return;
    }
    if (response.status === 1) {
       setSelecting(false);
      if (batchAction !== BatchAction.ASSOCIATE) {
        const hasDeviceErrors = response?.content?.error?.length;
        if (hasDeviceErrors) {
          for (let deviceError of response?.content?.error) {
            addToast(
              "error",
              `Erro na operação para o dispositivo ${deviceError?.deviceid}: ${
                deviceError?.error
              }`
            );
          }
        } else {
          addToast("success", "Operação realizada com sucesso!");
          setSelecting(false);
        }
      }
    } else {
      addToast("error", "Erro ao realizar a operação.");
    }

    setLoading(false);
    return response;
  }
  return (
    <div className="isp-data-card-container">
      <div>
        <div className="isp-data-card isp-info">
          <div className="subtitle">Dados do provedor </div>
          <div style={{ marginTop: "10px" }}>
            <Input
              name="name"
              label="Nome do provedor"
              value={isp.name}
            ></Input>
            <Input name="ispid" label="ISP ID" value={isp.ispid}></Input>
            <Input
              name="name"
              label="CNPJ do provedor"
              value={isp.cnpj}
            ></Input>
            <Input
              name="name"
              label="Telefone do provedor"
              value={isp.phone}
            ></Input>
          </div>
          <div style={{ textAlign: "left", padding: "0px 15px 10px" }}>
            <b>Quantidade de dispositivos: </b>
            {isp?.devices}
          </div>
          <BatchActionModal
            batchAction={batchAction}
            onSubmit={(value) => onSubmit(value)}
            setSelecting={setSelecting}
            selecting={selecting}
            loading={loading}
            profile={profile?.name}
          />
        </div>
        <div
          className="isp-data-card factory-default"
          style={{ marginTop: "30px" }}
        >
          <div className="subtitle">Perfil de padrão de fábrica</div>
          <div className="factory-default-info">
            <span>
              <b> Nome do perfil: </b> {profile?.name ? profile?.name : "-"}
            </span>
            <span>
              <b> ID do perfil: </b> {profile?.id ? profile?.id : "-"}
            </span>
          </div>
        </div>
      </div>
      <div className="isp-device-models">
        <div className="subtitle">Visão geral </div>
        <div className="model-charts-wrapper">
          {versionModels.total === 0 ? (
            <div className="no-device-found">
              Nenhum dispositivo encontrado.
            </div>
          ) : (
            versionModels.models.map((value) => composeModelCard(value))
          )}
        </div>
      </div>
    </div>
  );
}
